import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, TextField, Button, Paper } from '@mui/material';
import { supabase } from '../supabaseClient';

export default function AuthCallback() {
  const [status, setStatus] = useState<'loading' | 'email' | 'error'>('loading');
  const [email, setEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const handle = async () => {
      // Supabase OAuth 완료 후 세션 확인
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error || !session?.user) {
        setErrorMsg(error?.message || '인증에 실패했습니다.');
        setStatus('error');
        setTimeout(() => { window.location.href = '/login'; }, 3000);
        return;
      }

      const user = session.user;

      // profiles 테이블에 upsert (신규 유저 처리)
      const userEmail = user.email || null;
      await supabase.from('profiles').upsert({
        id: user.id,
        email: userEmail,
        role: 'user',
      }, { onConflict: 'id', ignoreDuplicates: true });

      // 이메일 없으면 입력 받기
      if (!userEmail) {
        setStatus('email');
        return;
      }

      redirectByRole(user.id);
    };

    handle();
  }, []);

  const redirectByRole = async (userId: string) => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', userId)
      .single();

    if (profile?.role === 'seller') {
      window.location.href = '/seller';
    } else if (profile?.role === 'creator') {
      window.location.href = '/creator';
    } else if (profile?.role === 'admin') {
      window.location.href = '/admin';
    } else {
      window.location.href = '/register';
    }
  };

  const handleEmailSave = async () => {
    if (!email.trim()) return;
    setEmailSaving(true);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      window.location.href = '/login';
      return;
    }

    await supabase.from('profiles').update({ email: email.trim() }).eq('id', session.user.id);
    await supabase.auth.updateUser({ email: email.trim() });

    setEmailSaving(false);
    redirectByRole(session.user.id);
  };

  if (status === 'loading') {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">로그인 처리 중...</Typography>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box sx={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Typography color="error" variant="h6">{errorMsg}</Typography>
        <Typography color="text.secondary">잠시 후 로그인 페이지로 이동합니다...</Typography>
      </Box>
    );
  }

  // 이메일 입력 모달
  return (
    <Box sx={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
      <Paper elevation={0} sx={{ p: 5, borderRadius: 3, maxWidth: 420, width: '100%', boxShadow: '0 2px 16px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={700} mb={1}>이메일 주소 입력</Typography>
        <Typography color="text.secondary" fontSize={14} mb={3}>
          알림 수신 및 계정 관리를 위해 이메일을 입력해주세요.
        </Typography>
        <TextField
          fullWidth
          type="email"
          placeholder="이메일 주소"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleEmailSave(); }}
          sx={{ mb: 2 }}
        />
        <Button
          fullWidth
          variant="contained"
          onClick={handleEmailSave}
          disabled={emailSaving || !email.trim()}
          sx={{ py: 1.2, fontWeight: 700, bgcolor: '#111', '&:hover': { bgcolor: '#222' } }}
        >
          {emailSaving ? '저장 중...' : '시작하기'}
        </Button>
      </Paper>
    </Box>
  );
}
